package app.bloomey.web.core.offCanvas.items

import androidx.compose.runtime.*
import app.bloomey.web.components.modals.Toast
import app.bloomey.web.components.widgets.*
import app.bloomey.web.core.data.models.Category
import app.bloomey.web.core.data.models.CategoryUpdateRequest
import app.bloomey.web.core.modal.LocalSiteModelController
import app.bloomey.web.core.modal.modals.DeleteItemModal
import app.bloomey.web.core.offCanvas.LocalOffCanvasController
import app.bloomey.web.core.offCanvas.OffCanvasItem
import app.bloomey.web.core.res.Res
import app.bloomey.web.core.res.icClose
import app.bloomey.web.core.res.icTrash
import app.bloomey.web.modules.library.LibraryContract
import app.bloomey.web.modules.library.LibraryViewModel
import app.bloomey.web.theme.SpanTextHeader1Variant
import app.bloomey.web.theme.toSitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.ArrowForwardIcon
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.SilkTheme
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.border
import kotlinx.coroutines.flow.collectLatest
import kotlinx.datetime.Instant
import kotlinx.datetime.toJSDate
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

class LibraryOffCanvas(private val category: Category, val onModified: () -> Unit) : OffCanvasItem {
    override val pathId: String
        get() = "library:$category"

    @Composable
    override fun Content(modifier: Modifier) {
        val sitePalette = ColorMode.current.toSitePalette()
        val offCanvasController = LocalOffCanvasController.current
        val siteModalController = LocalSiteModelController.current
        var color by remember { mutableStateOf(category.color) }
        var title by remember { mutableStateOf(category.title) }
        var description by remember { mutableStateOf(category.description) }
        val scope = rememberCoroutineScope()
        val viewModel = remember { LibraryViewModel(scope) }
        val uiState by viewModel.observeStates().collectAsState()
        LaunchedEffect(Unit) {
            viewModel.observeStates().collectLatest {
                if (it.deleteLibrary.isSuccess || it.updateLibrary.isSuccess) {
                    offCanvasController.pop(this@LibraryOffCanvas)
                    onModified()
                }
            }
        }
        Column(
            modifier = modifier.padding(40.px).backgroundColor(sitePalette.background)
        ) {
            Row(Modifier.fillMaxWidth()) {
                SvgLoader(
                    Res.Assets.icClose,
                    modifier = Modifier.size(24.px).cursor(Cursor.Pointer).onClick {
                        offCanvasController.pop(this@LibraryOffCanvas)
                    },
                    color = sitePalette.onBackgroundLight
                )
                Spacer()
                if (uiState.deleteLibrary.isLoading) {
                    LoadingView(size = 24.px, modifier = Modifier.maxSize(24.px))
                } else
                    SvgLoader(
                        Res.Assets.icTrash,
                        modifier = Modifier.size(24.px)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                siteModalController.push(
                                    DeleteItemModal(
                                        title = "Delete Library",
                                        text = "Are you sure you want to delete this library?",
                                        onDelete = {
                                            viewModel.trySend(LibraryContract.Inputs.DeleteLibrary(libraryId = category.id))
                                        }
                                    )
                                )
                            },
                        color = Colors.Red
                    )
            }
            Height(20.px)
            SpanText("Library", variant = SpanTextHeader1Variant)
            Height(4.px)
            SpanText("#${category.id}")
            Height(4.px)
            Row(modifier = Modifier.flexWrap(FlexWrap.Wrap)) {
                SpanText("User Id: ")
                SpanText(
                    category.ownerId, modifier = Modifier
                        .color(sitePalette.brand.primary)
                        .onClick {
                            offCanvasController.push(UserOffCanvas(userId = category.ownerId))
                        }.cursor(Cursor.Pointer)
                )
            }
            Height(20.px)
            Column(modifier = Modifier.gap(20.px).fillMaxWidth()) {
                SiteTextInput(label = "Color", text = color, modifier = Modifier.fillMaxWidth()) {
                    if (it.length <= 7)
                        color = it
                }
                SiteTextInput(label = "Title", text = title, modifier = Modifier.fillMaxWidth()) {
                    title = it
                }

                SiteTextInput(label = "Description", text = description, modifier = Modifier.fillMaxWidth()) {
                    description = it
                }

                Row(
                    modifier = Modifier.fillMaxWidth()
                        .border(1.px, color = SilkTheme.palette.border, style = LineStyle.Solid).borderRadius(8.px)
                        .padding(leftRight = 8.px, topBottom = 12.px)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            offCanvasController.push(LibraryVideosOffCanvasItem(category.id, category))
                        }, verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Videos  ${category.videoCount}")
                    Spacer()
                    ArrowForwardIcon(modifier = Modifier.height(20.px))
                }

                SpanText(
                    "Created At: ${
                        Instant.fromEpochMilliseconds(category.createdAt).toJSDate().toDateString()
                    }"
                )
                category.lastWatched?.dateTime?.let {
                    SpanText("Last Watched: ${Instant.fromEpochMilliseconds(it).toJSDate().toDateString()}")
                }
            }
            Spacer()
            SiteButton(
                loading = uiState.updateLibrary.isLoading,
                onClick = {
                    viewModel.trySend(
                        LibraryContract.Inputs.UpdateLibrary(
                            category.id,
                            request = CategoryUpdateRequest(
                                image = category.image,
                                color = color,
                                title = title,
                                description = description
                            )
                        )
                    )
                }, modifier = Modifier.fillMaxWidth()
            ) {
                SpanText("Update")
            }

        }
        Toast(message = uiState.deleteLibrary.getMessage ?: "", show = uiState.deleteLibrary.isError) {

        }

        Toast(message = uiState.updateLibrary.getMessage ?: "", show = uiState.updateLibrary.isError) {
            viewModel.trySend(LibraryContract.Inputs.ResetState(LibraryContract.State::updateLibrary.name))
        }
    }

}